import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center pa-4" }
const _hoisted_2 = { class: "d-flex align-center" }
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = {
  key: 1,
  class: "d-flex gap-4"
}
const _hoisted_5 = { class: "mb-4" }
const _hoisted_6 = {
  key: 0,
  class: "mb-3"
}
const _hoisted_7 = { class: "image-preview-container" }
const _hoisted_8 = ["src"]
const _hoisted_9 = {
  key: 1,
  class: "mb-3"
}
const _hoisted_10 = { class: "image-preview-container" }
const _hoisted_11 = ["src"]

import { ref, computed, watch } from 'vue';
import { useUserStore } from '@/api/store/user';
import { useSettingsStore } from '@/api/store/settings';
import { healthDiaryService, type DiaryEntry } from "@/api/services/healthDiary";
import { hexToRgba } from "@/tools/design";
import DrinkIcon from "@/components/Icons/DrinkIcon.vue";
import FeelIcon from "@/components/Icons/FeelIcon.vue";
import DidIcon from "@/components/Icons/DidIcon.vue";
import ExerciseIcon from "@/components/Icons/ExerciseIcon.vue";

interface Props {
  modelValue: boolean;
  entryType?: string | null;
  existingEntry?: DiaryEntry | null;
  isEditing?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DiaryEntryForm',
  props: {
    modelValue: { type: Boolean },
    entryType: {},
    existingEntry: {},
    isEditing: { type: Boolean }
  },
  emits: ["update:modelValue", "entry-created", "entry-updated"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const settingsStore = useSettingsStore();

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const formData = ref<Omit<DiaryEntry, 'id' | 'read_by_practitioner'>>({
  entry_datetime: new Date().toISOString(),
  entry_type: '',
  ate_content: '',
  noticed_content: ''
});

  const isSubmitting = ref(false);
  const formError = ref<string | null>(null);
  const selectedImage = ref<File | null>(null);
  const imagePreviewUrl = ref<string>('');
  const removeExistingImageFlag = ref(false);
  const fileInput = ref<HTMLInputElement>();

// Dynamic label based on entry type
const dynamicLabel = computed(() => {
  switch (props.entryType) {
    case 'ate':
      return 'I ate...';
    case 'drank':
      return 'I drank...';
    case 'exercised':
      return 'I exercised...';
    case 'did':
      return 'I did...';
    case 'slept':
      return 'I slept...';
    case 'felt':
      return 'I feel...';
    default:
      return 'I ate...';
  }
});

// Dynamic label for noticed content
const noticedLabel = computed(() => {
  return props.entryType === 'felt' ? 'I noticed...' : 'I felt...';
});

// Entry icon based on type
const entryIcon = computed(() => {
  switch (props.entryType) {
    case 'ate':
      return 'fa fa-cutlery';
    case 'slept':
      return 'fa fa-bed';
    default:
      return 'fa fa-cutlery';
  }
});

const isEditingDateTime = ref(false);

const dateValue = computed({
  get: () => {
    return formData.value.entry_datetime.split('T')[0];
  },
  set: (value) => {
    const [hours, minutes] = timeValue.value.split(':').map(Number);
    const newDate = new Date(value);
    newDate.setHours(hours, minutes, 0, 0);
    formData.value.entry_datetime = newDate.toISOString();
  }
});

const timeValue = computed({
  get: () => {
    const date = new Date(formData.value.entry_datetime);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  },
  set: (value) => {
    const datePart = dateValue.value;
    const [hours, minutes] = value.split(':').map(Number);
    const newDate = new Date(datePart);
    newDate.setHours(hours, minutes, 0, 0);
    formData.value.entry_datetime = newDate.toISOString();
  }
});

const checkBlur = (_e: FocusEvent) => {
  setTimeout(() => {
    const dateInput = document.querySelector('input[type="date"]');
    const timeInput = document.querySelector('input[type="time"]');
    if (!dateInput?.contains(document.activeElement) && !timeInput?.contains(document.activeElement)) {
      isEditingDateTime.value = false;
    }
  }, 100);
};

// Format the current date and time
const formattedDateTime = computed(() => {
  const date = new Date(formData.value.entry_datetime);
  return date.toLocaleString('default', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
});

const isFormValid = computed(() => {
  return !!formData.value.ate_content?.trim();
});

const buttonText = computed(() => props.isEditing ? 'Save Changes' : 'Submit');

const existingImageUrl = computed(() => {
  if (removeExistingImageFlag.value) return null;
  return props.existingEntry?.file?.file_path || null;
});

// Image handling methods
const handleImageSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (file) {
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      formError.value = 'Image file size must be less than 10MB';
      return;
    }
    
    selectedImage.value = file;
    imagePreviewUrl.value = URL.createObjectURL(file);
    formError.value = null;
  }
};

  const removeSelectedImage = () => {
    selectedImage.value = null;
    imagePreviewUrl.value = '';
    
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  };

const removeExistingImage = () => {
  removeExistingImageFlag.value = true;
};

const closeDialog = () => {
  dialog.value = false;
  formError.value = null;
  removeSelectedImage();
  removeExistingImageFlag.value = false;
  
  if (!props.isEditing) {
    formData.value = {
      entry_datetime: new Date().toISOString(),
      entry_type: '',
      ate_content: '',
      noticed_content: ''
    };
  }
};

const submitForm = async () => {
  if (!isFormValid.value) return;

  isSubmitting.value = true;
  formError.value = null;

  try {
    formData.value.entry_type = props.entryType || undefined;
    
    // Create FormData if we have an image to upload
    const hasImageToUpload = selectedImage.value || removeExistingImageFlag.value;
    
    if (hasImageToUpload) {
      const formDataToSend = new FormData();
      formDataToSend.append('entry_datetime', formData.value.entry_datetime);
      formDataToSend.append('entry_type', formData.value.entry_type || '');
      formDataToSend.append('ate_content', formData.value.ate_content || '');
      formDataToSend.append('noticed_content', formData.value.noticed_content || '');
      
      if (selectedImage.value) {
        formDataToSend.append('image', selectedImage.value);
      }
      
      if (props.isEditing && props.existingEntry?.id) {
        await healthDiaryService.update(props.existingEntry.id, formDataToSend);
        emit('entry-updated', formData.value.entry_datetime);
      } else {
        await healthDiaryService.create(formDataToSend);
        emit('entry-created', formData.value.entry_datetime);
      }
    } else {
      // No image changes, send as regular JSON
      if (props.isEditing && props.existingEntry?.id) {
        await healthDiaryService.update(props.existingEntry.id, formData.value);
        emit('entry-updated', formData.value.entry_datetime);
      } else {
        await healthDiaryService.create(formData.value);
        emit('entry-created', formData.value.entry_datetime);
      }
    }
    
    closeDialog();
  } catch (err: any) {
    console.error('Error saving diary entry:', err);
    formError.value = err.response?.data?.message || 'Failed to save diary entry. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

// Watch for existing entry changes to update form data
watch(() => props.existingEntry, (newEntry) => {
  if (newEntry) {
    formData.value = {
      entry_datetime: newEntry.entry_datetime,
      entry_type: newEntry.entry_type,
      ate_content: newEntry.ate_content || '',
      noticed_content: newEntry.noticed_content || ''
    };
  }
}, { immediate: true });

// Watch for entry type changes to update form data
watch(() => props.entryType, (newType) => {
  if (newType) {
    formData.value.entry_type = newType;
  }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: dialog.value,
    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((dialog).value = $event)),
    width: "500"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, {
        class: "diary-entry-form",
        rounded: "lg"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              (__props.entryType === 'drank')
                ? (_openBlock(), _createBlock(DrinkIcon, {
                    key: 0,
                    color: _unref(settingsStore).settings.buttonColor,
                    class: "me-4"
                  }, null, 8, ["color"]))
                : (__props.entryType === 'felt')
                  ? (_openBlock(), _createBlock(FeelIcon, {
                      key: 1,
                      color: _unref(settingsStore).settings.buttonColor,
                      class: "me-4"
                    }, null, 8, ["color"]))
                  : (__props.entryType === 'did')
                    ? (_openBlock(), _createBlock(DidIcon, {
                        key: 2,
                        color: _unref(settingsStore).settings.buttonColor,
                        class: "me-4"
                      }, null, 8, ["color"]))
                    : (__props.entryType === 'exercised')
                      ? (_openBlock(), _createBlock(ExerciseIcon, {
                          key: 3,
                          color: _unref(settingsStore).settings.buttonColor,
                          class: "me-4"
                        }, null, 8, ["color"]))
                      : (_openBlock(), _createBlock(_component_v_icon, {
                          key: 4,
                          icon: entryIcon.value,
                          size: "24",
                          color: _unref(settingsStore).settings.buttonColor,
                          class: "me-4"
                        }, null, 8, ["icon", "color"])),
              _createElementVNode("span", {
                class: "text-h6",
                style: _normalizeStyle({ color: _unref(settingsStore).settings.buttonColor })
              }, _toDisplayString(dynamicLabel.value), 5)
            ]),
            _createVNode(_component_v_btn, {
              icon: "",
              variant: "text",
              onClick: closeDialog
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { icon: "fa fa-times" })
              ]),
              _: 1
            })
          ]),
          _createVNode(_component_v_divider),
          _createVNode(_component_v_card_text, { class: "pa-4" }, {
            default: _withCtx(() => [
              (formError.value)
                ? (_openBlock(), _createBlock(_component_v_alert, {
                    key: 0,
                    type: "error",
                    class: "mb-4"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(formError.value), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_form, {
                onSubmit: _withModifiers(submitForm, ["prevent"])
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    (!isEditingDateTime.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (isEditingDateTime.value = true)),
                          class: "text-caption text-grey-darken-1 cursor-pointer d-flex align-center"
                        }, [
                          _createElementVNode("span", null, _toDisplayString(formattedDateTime.value), 1),
                          _createVNode(_component_v_icon, {
                            size: "small",
                            class: "ms-2"
                          }, {
                            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                              _createTextVNode("fa fa-pencil", -1)
                            ]))]),
                            _: 1
                          })
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                          _createVNode(_component_v_text_field, {
                            modelValue: dateValue.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dateValue).value = $event)),
                            type: "date",
                            label: "Date",
                            variant: "outlined",
                            density: "compact",
                            max: new Date().toISOString().split('T')[0],
                            onBlur: checkBlur
                          }, null, 8, ["modelValue", "max"]),
                          _createVNode(_component_v_text_field, {
                            modelValue: timeValue.value,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((timeValue).value = $event)),
                            type: "time",
                            label: "Time",
                            variant: "outlined",
                            density: "compact",
                            onBlur: checkBlur
                          }, null, 8, ["modelValue"])
                        ]))
                  ]),
                  _createVNode(_component_v_textarea, {
                    modelValue: formData.value.ate_content,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.ate_content) = $event)),
                    label: dynamicLabel.value,
                    variant: "outlined",
                    rows: "4",
                    required: "",
                    rules: [v => !!v || 'This field is required'],
                    class: "mb-4"
                  }, null, 8, ["modelValue", "label", "rules"]),
                  _createVNode(_component_v_textarea, {
                    modelValue: formData.value.noticed_content,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.noticed_content) = $event)),
                    label: noticedLabel.value,
                    style: _normalizeStyle(`label-color: ${_unref(settingsStore).settings.primaryColor}`),
                    variant: "outlined",
                    rows: "4",
                    placeholder: "Optional",
                    class: "mb-4"
                  }, null, 8, ["modelValue", "label", "style"]),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", {
                      class: "text-subtitle-2 mb-2",
                      style: _normalizeStyle({ color: _unref(settingsStore).settings.primaryColor })
                    }, " Add Photo (Optional) ", 4),
                    (existingImageUrl.value && !selectedImage.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createElementVNode("div", _hoisted_7, [
                            _createElementVNode("img", {
                              src: existingImageUrl.value,
                              alt: "Diary entry image",
                              class: "existing-image"
                            }, null, 8, _hoisted_8),
                            _createVNode(_component_v_btn, {
                              icon: "mdi-close",
                              size: "x-small",
                              color: "error",
                              class: "remove-image-btn",
                              onClick: removeExistingImage
                            }, {
                              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                _createTextVNode(" × ", -1)
                              ]))]),
                              _: 1
                            })
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    (selectedImage.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          _createElementVNode("div", _hoisted_10, [
                            _createElementVNode("img", {
                              src: imagePreviewUrl.value,
                              alt: "Selected image",
                              class: "image-preview"
                            }, null, 8, _hoisted_11),
                            _createVNode(_component_v_btn, {
                              size: "x-small",
                              color: "error",
                              class: "remove-image-btn",
                              onClick: removeSelectedImage
                            }, {
                              default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                _createTextVNode(" × ", -1)
                              ]))]),
                              _: 1
                            })
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("input", {
                      ref_key: "fileInput",
                      ref: fileInput,
                      type: "file",
                      accept: "image/*",
                      style: {"display":"none"},
                      onChange: handleImageSelect
                    }, null, 544),
                    (!selectedImage.value && !existingImageUrl.value)
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 2,
                          variant: "outlined",
                          color: _unref(settingsStore).settings.primaryColor,
                          onClick: _cache[5] || (_cache[5] = ($event: any) => (fileInput.value?.click())),
                          class: "mb-2"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                _createTextVNode("fa fa-camera", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[12] || (_cache[12] = _createTextVNode(" Choose Photo ", -1))
                          ]),
                          _: 1
                        }, 8, ["color"]))
                      : (_openBlock(), _createBlock(_component_v_btn, {
                          key: 3,
                          variant: "outlined",
                          color: _unref(settingsStore).settings.primaryColor,
                          onClick: _cache[6] || (_cache[6] = ($event: any) => (fileInput.value?.click())),
                          class: "mb-2"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                _createTextVNode("fa fa-camera", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[14] || (_cache[14] = _createTextVNode(" Change Photo ", -1))
                          ]),
                          _: 1
                        }, 8, ["color"])),
                    _cache[15] || (_cache[15] = _createElementVNode("div", { class: "text-caption text-grey-darken-1" }, " Supported formats: JPG, PNG, GIF, WebP (Max 10MB) ", -1))
                  ]),
                  (props.existingEntry?.practitioner_comment)
                    ? (_openBlock(), _createBlock(_component_v_card, {
                        key: 0,
                        class: "practitioner-comment mb-4",
                        variant: "flat",
                        density: "comfortable",
                        style: _normalizeStyle({
              backgroundColor: _unref(hexToRgba)(_unref(settingsStore).settings.primaryColor, 0.1),
              border: `1px solid ${_unref(hexToRgba)(_unref(settingsStore).settings.primaryColor, 0.2)}`
            })
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_title, {
                            class: "text-subtitle-2 pb-1 font-weight-bold",
                            style: _normalizeStyle({ color: _unref(settingsStore).settings.primaryColor })
                          }, {
                            default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                              _createTextVNode(" Practitioner Comment ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["style"]),
                          _createVNode(_component_v_card_text, {
                            class: "pt-0",
                            style: {"color":"black"}
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(props.existingEntry.practitioner_comment), 1)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["style"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, { class: "pa-4" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                style: _normalizeStyle({
            backgroundColor: _unref(settingsStore).settings.buttonColor,
            color: 'white'
          }),
                class: "text-white primary-button",
                rounded: "lg",
                block: "",
                onClick: submitForm,
                disabled: !isFormValid.value || isSubmitting.value,
                loading: isSubmitting.value
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(buttonText.value), 1)
                ]),
                _: 1
              }, 8, ["style", "disabled", "loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})